import { ReactElement } from 'react';
import { translate } from '@micro-apps/env';
import BevSafeText from '../../bev-safe-text';

type CalendarMetaProps = {
	currencyCode?: string;
	layout?: 'mobile' | 'desktop';
	nightlyRatesExperiment?: boolean;
};

function LimitedLegendIcon({
	nightlyRatesExperiment,
}: {
	nightlyRatesExperiment?: boolean;
}): ReactElement {
	if (nightlyRatesExperiment) {
		return (
			<span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">
				<span className="block h-[6px] w-[6px] rounded-full bg-neutral-700" />
			</span>
		);
	}

	return (
		<span
			aria-hidden="true"
			className="box-border block h-4 w-4 shrink-0 rounded-full border border-dashed border-button-primary"
		/>
	);
}

function UnavailableLegendIcon(): ReactElement {
	return (
		<span aria-hidden="true" className="relative flex h-4 w-4 shrink-0 items-center justify-center">
			<span className="absolute top-1/2 left-0 h-px w-full -translate-y-1/2 bg-button-primary-disabled" />
		</span>
	);
}

function LegendItem({
	label,
	nightlyRatesExperiment,
	unavailable = false,
}: {
	label: string;
	nightlyRatesExperiment?: boolean;
	unavailable?: boolean;
}): ReactElement {
	return (
		<div className="flex flex-row items-center gap-2.5">
			{unavailable ? (
				<UnavailableLegendIcon />
			) : (
				<LimitedLegendIcon nightlyRatesExperiment={nightlyRatesExperiment} />
			)}
			<span
				className="text-caption text-text-secondary leading-none"
				style={{ transform: 'translateY(2px)' }}
			>
				<BevSafeText>{label}</BevSafeText>
			</span>
		</div>
	);
}

export default function CalendarMeta({
	currencyCode,
	layout = 'desktop',
	nightlyRatesExperiment,
}: CalendarMetaProps): ReactElement<any> {
	const legend = (
		<>
			<LegendItem label={translate('Limited')} nightlyRatesExperiment={nightlyRatesExperiment} />
			<LegendItem label={translate('Unavailable')} unavailable />
		</>
	);

	if (layout === 'mobile') {
		return (
			<div className="flex w-full flex-col items-center spacing-stack-s">
				<div className="flex flex-row items-center justify-center gap-5">{legend}</div>
				{nightlyRatesExperiment && currencyCode && (
					<p className="text-center text-caption text-text-secondary">
						From {currencyCode} per night. Taxes and fees may apply.
					</p>
				)}
			</div>
		);
	}

	return (
		<div className="w-full">
			<div className="flex w-full flex-row items-center justify-between gap-2">
				<div className="flex flex-row items-center gap-[30px]">{legend}</div>
				{nightlyRatesExperiment && currencyCode && (
					<div className="shrink-0 text-right text-caption text-text-secondary">
						From {currencyCode} per night. Taxes and fees may apply.
					</div>
				)}
			</div>
		</div>
	);
}
